import React, { Fragment, ReactNode, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import BackgroundImage from 'src/assets/images/background.svg?react'
import PoweredLogoWhite from 'src/assets/images/poweredby-white.svg'
import { useDispatch, useSelector } from 'react-redux'
import { getPartnerPublicInfo } from 'src/selectors/partner'
import { useParams } from 'react-router-dom'
import { FetchPartnerPublicInfo } from 'src/constants/actionTypes'
import { LoadingPage } from 'src/pages/LoadingPage'
import { getDriver } from 'src/selectors/driver'
import LogoWhite from 'src/assets/images/gigsafe-logo-white.svg'
import { ChatMajor, ChecklistAlternateMajor, QuestionMarkMajor } from '@shopify/polaris-icons'
import { Typography } from '@mui/material'
import { Settings } from 'src/components/settings/Settings'
import { Button } from 'src/components/shared/Button'
import { GetHelpModal } from 'src/components/settings/GetHelpModal'
import { show as showIntercom } from '@intercom/messenger-js-sdk'
import { useAuthTokens } from 'src/hooks/useAuthTokens'
import { useMediaQueries } from 'src/hooks/useMediaQueries'
import { GigSafeMemberBenefitPortal } from 'src/components/shared/GigSafeMemberBenefitPortal'
import { getIsTokenSignIn } from 'src/selectors/auth'
import { RemoteConfigBanner } from 'src/components/shared/RemoteConfigBanner'
import { RemoteConfigKey } from 'src/constants/remoteConfig'

interface Props {
  header?: ReactNode
  children: ReactNode
  isShowGigSafeMemberBenefitPortal?: boolean
  fullHeightCard?: boolean
  hideOnboardingBadge?: boolean
}

export const ClientPublicWrapper = ({
  children,
  header,
  isShowGigSafeMemberBenefitPortal = false,
  fullHeightCard = false,
  hideOnboardingBadge = false,
}: Props) => {
  const params = useParams()
  const dispatch = useDispatch()

  const partnerPublic = useSelector(getPartnerPublicInfo)
  const driver = useSelector(getDriver)
  const isIsTokenSignIn = useSelector(getIsTokenSignIn)

  const { authToken } = useAuthTokens()
  const { isMobile, isDesktopOrTablet } = useMediaQueries()

  const [isGetHelpOpen, setIsGetHelpOpen] = useState(false)
  const [bannerHeight, setBannerHeight] = useState(0)

  const openGetHelp = useCallback(() => {
    setIsGetHelpOpen(true)
  }, [])

  const closeGetHelp = useCallback(() => {
    setIsGetHelpOpen(false)
  }, [])

  const handleGetHelpClick = useCallback(() => {
    if (driver) {
      showIntercom()

      return
    }

    openGetHelp()
  }, [driver, openGetHelp])

  useEffect(() => {
    if (params?.partnerName && !partnerPublic?.name) {
      dispatch(FetchPartnerPublicInfo.request({ partnerName: params?.partnerName }))
    }
  }, [params?.partnerName, partnerPublic?.name])

  if (params?.partnerName && !partnerPublic?.name) {
    return <LoadingPage flow="client" />
  }

  return (
    <Fragment>
      <div css={tw`h-full w-full flex flex-col`}>
        {!isIsTokenSignIn && (
          <div css={tw`w-full shadow fixed z-50`}>
            <div css={tw`bg-primary-700 w-full h-16 p-4 top-0 flex justify-between items-center`}>
              <img src={partnerPublic?.logoUrl || LogoWhite} css={tw`h-[28px]`} />
              <div css={tw`flex-row flex gap-2`}>
                <Button
                  className={tw`w-[120px] px-3`}
                  rightIcon={
                    isMobile && <QuestionMarkMajor css={tw`fill-shades-0`} width={20} height={20} />
                  }
                  leftIcon={
                    isDesktopOrTablet && (
                      <ChatMajor css={tw`fill-shades-0`} width={20} height={20} />
                    )
                  }
                  onClick={handleGetHelpClick}
                  color={isDesktopOrTablet ? 'dark' : 'primary'}
                  size="medium"
                  data-testid="header-help-open-btn"
                >
                  Get Help
                </Button>
                <Settings flow="client" />
              </div>
            </div>

            {authToken && !hideOnboardingBadge && (
              <div css={tw`bg-primary-100 w-full h-9 flex items-center justify-center flex-row`}>
                <ChecklistAlternateMajor width={20} />
                <Typography css={tw`ml-2`} variant="caption" fontWeight={600}>
                  Contractor's onboarding
                </Typography>
              </div>
            )}
            <RemoteConfigBanner
              configKey={RemoteConfigKey.ClientBanner}
              className={tw`rounded-none`}
              onHeightChange={setBannerHeight}
            />
          </div>
        )}
        <div css={tw`shrink-0`} style={{ height: bannerHeight }} />

        <div
          css={[
            tw`flex flex-1 bg-primary-800 items-center justify-center flex-col`,
            fullHeightCard
              ? tw`min-h-0 overflow-hidden pb-0 pt-16 lg:pb-6 lg:pt-24`
              : tw`pb-4 lg:pb-6 pt-20 lg:pt-[112px]`,
          ]}
        >
          {(!fullHeightCard || isDesktopOrTablet) && (
            <BackgroundImage css={tw`fixed top-0 bottom-0 z-0 h-full [&>path]:fill-primary-900`} />
          )}
          <div />
          <div
            css={[
              tw`bg-shades-0 w-full z-10 overflow-hidden flex flex-col`,
              fullHeightCard
                ? tw`flex-1 min-h-0 max-w-none mx-0 rounded-none lg:max-w-[390px] lg:mx-4 lg:rounded-2xl`
                : tw`max-w-[390px] mx-4 rounded-2xl`,
            ]}
          >
            {header}
            <div
              css={[
                header ? tw`px-4 pb-4` : tw`px-4 pt-6 pb-4`,
                fullHeightCard && tw`flex-1 flex flex-col overflow-y-auto min-h-0`,
              ]}
            >
              {children}
            </div>
          </div>
          {isShowGigSafeMemberBenefitPortal && (
            <div css={tw`my-4 mx-4 z-10 max-w-[390px]`}>
              <GigSafeMemberBenefitPortal />
            </div>
          )}
          {(!fullHeightCard || isDesktopOrTablet) && (
            <div css={tw`flex items-center mt-6 justify-center w-full z-20`}>
              <img src={PoweredLogoWhite} />
            </div>
          )}
        </div>
      </div>
      <GetHelpModal isOpen={isGetHelpOpen} onClose={closeGetHelp} />
    </Fragment>
  )
}
